import { useMachine } from "@xstate/react";
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { attentionMachine } from "../machines/attentionMachine";
import { useAppStore } from "../stores/useAppStore";
import { STIMULI } from "../lib/stimuli";
import { blankHeat, cellFromClientPoint, inferOracle, summarizeHeat, topCells } from "../lib/attention";
import { db } from "../lib/db";
import { roomWebSocketUrl, submitSession } from "../lib/api";
import { CELL_COUNT, HeatCell, LocalSession, ROUND_MS, SelfReport } from "../types";
import { uid } from "../lib/random";
import { HeatCanvas } from "./HeatCanvas";

function zoneFromIndex(i: number): SelfReport["ignoredZone"] {
  const row = Math.floor(i / 12);
  const col = i % 12;
  if (row <= 1) return "top";
  if (row >= 6) return "bottom";
  if (col <= 2) return "left";
  if (col >= 9) return "right";
  return "center";
}

export function AttentionArena() {
  const [state, send] = useMachine(attentionMachine);
  const { deviceHash, attest, roomId, wsState, commons, setWsState, setCommons, mergeCommonsPatch, setLastError } = useAppStore();
  const boardRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [heat, setHeat] = useState<HeatCell[]>(() => blankHeat());
  const heatRef = useRef<HeatCell[]>(blankHeat());
  const currentCell = useRef<number | null>(null);
  const pointerDownAt = useRef<number | null>(null);
  const lastDwellTick = useRef<number>(0);
  const [roundStartedAt, setRoundStartedAt] = useState<number | null>(null);
  const [remainingMs, setRemainingMs] = useState(ROUND_MS);
  const [remembered, setRemembered] = useState<number[]>([]);
  const [ignoredProbe, setIgnoredProbe] = useState<number | null>(null);
  const [savedSession, setSavedSession] = useState<LocalSession | null>(null);

  const isRunning = state.matches("running");
  const isReveal = state.matches("reveal") || state.matches("completed");
  const metrics = useMemo(() => summarizeHeat(heat), [heat]);
  const oracle = useMemo(() => inferOracle(metrics, { remembered, ignoredZone: ignoredProbe == null ? undefined : zoneFromIndex(ignoredProbe) }), [metrics, remembered, ignoredProbe]);
  const topLocal = useMemo(() => topCells(heat, 6), [heat]);

  const connect = useCallback(() => {
    if (!deviceHash || !attest || wsRef.current?.readyState === WebSocket.OPEN) return;
    try {
      setWsState("connecting");
      const ws = new WebSocket(roomWebSocketUrl(roomId, deviceHash, attest));
      wsRef.current = ws;
      ws.onopen = () => setWsState("live");
      ws.onclose = () => setWsState("offline");
      ws.onerror = () => setWsState("error");
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data as string);
          if (message.type === "snapshot") setCommons(message.cells || []);
          if (message.type === "pulse") mergeCommonsPatch(message.cells || []);
        } catch {
          // ignore malformed room traffic
        }
      };
    } catch (error) {
      setWsState("error");
      setLastError(error instanceof Error ? error.message : String(error));
    }
  }, [attest, deviceHash, mergeCommonsPatch, roomId, setCommons, setLastError, setWsState]);

  useEffect(() => {
    if (deviceHash && attest) connect();
    return () => {
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [attest, connect, deviceHash]);

  const resetRound = useCallback(() => {
    const fresh = blankHeat();
    heatRef.current = fresh;
    setHeat(fresh);
    setRemembered([]);
    setIgnoredProbe(null);
    setSavedSession(null);
    setRemainingMs(ROUND_MS);
    setRoundStartedAt(null);
    currentCell.current = null;
    pointerDownAt.current = null;
    lastDwellTick.current = performance.now();
  }, []);

  const startRound = useCallback(() => {
    resetRound();
    setRoundStartedAt(Date.now());
    lastDwellTick.current = performance.now();
    send({ type: "START" });
  }, [resetRound, send]);

  useEffect(() => {
    if (!isRunning) return;
    let frame = 0;
    const tick = (now: number) => {
      const dt = Math.min(320, now - lastDwellTick.current);
      lastDwellTick.current = now;
      const cellIndex = currentCell.current;
      if (cellIndex != null) {
        const next = heatRef.current.map((cell) => ({ ...cell }));
        next[cellIndex].dwellMs += dt;
        next[cellIndex].lastAt = Date.now();
        if (!next[cellIndex].firstAt) next[cellIndex].firstAt = Date.now();
        heatRef.current = next;
        setHeat(next);
      }
      if (roundStartedAt) {
        const remaining = Math.max(0, ROUND_MS - (Date.now() - roundStartedAt));
        setRemainingMs(remaining);
        if (remaining <= 0) {
          send({ type: "STOP" });
          return;
        }
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [isRunning, roundStartedAt, send]);

  useEffect(() => {
    if (!isRunning) return;
    const interval = window.setInterval(() => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      const changed = heatRef.current
        .filter((cell) => cell.dwellMs > 0 || cell.touches > 0 || cell.visits > 0)
        .map((cell) => ({ i: cell.i, dwellMs: Math.round(cell.dwellMs / 12), touches: cell.touches, visits: cell.visits }))
        .slice(0, CELL_COUNT);
      if (changed.length) ws.send(JSON.stringify({ type: "pulse", cells: changed, sentAt: Date.now() }));
    }, 1400);
    return () => window.clearInterval(interval);
  }, [isRunning]);

  const noteCell = useCallback((index: number, kind: "visit" | "touch" | "long") => {
    const next = heatRef.current.map((cell) => ({ ...cell }));
    const cell = next[index];
    const now = Date.now();
    if (kind === "visit") cell.visits += 1;
    if (kind === "touch") cell.touches += 1;
    if (kind === "long") cell.longPresses += 1;
    cell.lastAt = now;
    if (!cell.firstAt) cell.firstAt = now;
    heatRef.current = next;
    setHeat(next);
  }, []);

  const onPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isRunning || !boardRef.current) return;
    const next = cellFromClientPoint(boardRef.current.getBoundingClientRect(), event.clientX, event.clientY);
    if (next !== currentCell.current) {
      currentCell.current = next;
      noteCell(next, "visit");
    }
  }, [isRunning, noteCell]);

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!boardRef.current) return;
    const index = cellFromClientPoint(boardRef.current.getBoundingClientRect(), event.clientX, event.clientY);
    if (isRunning) {
      pointerDownAt.current = performance.now();
      noteCell(index, "touch");
    } else if (isReveal) {
      if (remembered.length < 3 || remembered.includes(index)) {
        setRemembered((items) => (items.includes(index) ? items.filter((item) => item !== index) : [...items, index].slice(0, 3)));
      } else {
        setIgnoredProbe(index);
      }
    }
  }, [isReveal, isRunning, noteCell, remembered]);

  const onPointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!isRunning || pointerDownAt.current == null || !boardRef.current) return;
    const held = performance.now() - pointerDownAt.current;
    if (held > 520) {
      const index = cellFromClientPoint(boardRef.current.getBoundingClientRect(), event.clientX, event.clientY);
      noteCell(index, "long");
    }
    pointerDownAt.current = null;
  }, [isRunning, noteCell]);

  const saveRound = useCallback(async () => {
    if (!deviceHash || !attest || !roundStartedAt) return;
    const now = new Date().toISOString();
    const selfReport: SelfReport = {
      remembered,
      ignoredZone: ignoredProbe == null ? undefined : zoneFromIndex(ignoredProbe),
      surprise: remembered.length < 3 ? "memory partial" : "memory marked"
    };
    const summary = summarizeHeat(heatRef.current);
    const session: LocalSession = {
      id: uid("session"),
      roomId,
      deviceHash,
      createdAt: new Date(roundStartedAt).toISOString(),
      updatedAt: now,
      consentVersion: "afterglow-atlas-consent-v1",
      metrics: summary,
      selfReport,
      oracle: inferOracle(summary, selfReport),
      tags: ["attention", "dwell", "touch", "commons"],
      meta: {
        phenomenon: "change blindness + inhibition of return",
        input: "pointer/touch dwell only; no camera; no eye tracking"
      }
    };
    await db.sessions.put(session);
    setSavedSession(session);
    send({ type: "SAVE" });
    try {
      const response = await submitSession(session, attest);
      setCommons(response.commons.cells);
    } catch (error) {
      setLastError(error instanceof Error ? error.message : String(error));
    }
  }, [attest, deviceHash, heatRef, ignoredProbe, remembered, roomId, roundStartedAt, send, setCommons, setLastError]);

  const progress = Math.round((1 - remainingMs / ROUND_MS) * 100);

  return (
    <section className="arena-shell" aria-labelledby="arena-title">
      <div className="arena-copy">
        <p className="eyebrow">No camera. No eye tracking. Touch + dwell only.</p>
        <h1 id="arena-title">Afterglow Atlas</h1>
        <p>
          A 45‑second attention instrument disguised as a little omen field. Touch what tugs at you, linger where your mind sticks, and the app builds a proxy gaze map from your hands.
        </p>
      </div>

      <div className="control-strip">
        <button className="primary" onClick={startRound} disabled={!attest || isRunning}>
          {state.matches("completed") ? "Run another atlas" : "Start 45s run"}
        </button>
        <button onClick={() => send({ type: "STOP" })} disabled={!isRunning}>Reveal heatmap</button>
        <button onClick={() => { resetRound(); send({ type: "RESET" }); }}>Reset</button>
        <span className={`status-pill ${wsState}`}>WebSocket: {wsState}</span>
        <span className="status-pill">{state.value.toString()}</span>
        <span className="timer" aria-live="polite">{isRunning ? `${Math.ceil(remainingMs / 1000)}s` : `${progress}% trace`}</span>
      </div>

      <div className="board-wrap">
        <div
          ref={boardRef}
          className={`stimulus-board ${isRunning ? "running" : ""} ${isReveal ? "reveal" : ""}`}
          onPointerMove={onPointerMove}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerLeave={() => { currentCell.current = null; }}
          role="application"
          aria-label="Interactive attention field"
        >
          <HeatCanvas heat={heat} commons={commons} />
          {STIMULI.map((stimulus) => {
            const rememberedMark = remembered.includes(stimulus.index);
            const ignoredMark = ignoredProbe === stimulus.index;
            const hot = topLocal.includes(stimulus.index);
            return (
              <article
                key={stimulus.id}
                className={`stimulus ${stimulus.family} ${rememberedMark ? "remembered" : ""} ${ignoredMark ? "ignored" : ""} ${hot ? "hot" : ""}`}
                style={{ "--salience": stimulus.salience } as CSSProperties}
                title={`${stimulus.title}: ${stimulus.trick}`}
              >
                <span className="glyph" aria-hidden="true">{stimulus.glyph}</span>
                <strong>{stimulus.title}</strong>
                <small>{stimulus.body}</small>
              </article>
            );
          })}
        </div>
      </div>

      {isReveal && (
        <div className="oracle-panel">
          <div>
            <p className="eyebrow">Your inferred gaze</p>
            <h2>{oracle.name}</h2>
            <p>{oracle.line}</p>
            <p className="small muted">Now tap up to three tiles you remember. Tap a fourth tile to mark a zone you think you ignored.</p>
          </div>
          <dl className="metric-grid">
            <div><dt>coverage</dt><dd>{Math.round(metrics.coverage * 100)}%</dd></div>
            <div><dt>entropy</dt><dd>{Math.round(metrics.entropy * 100)}%</dd></div>
            <div><dt>dwell</dt><dd>{Math.round(metrics.totalDwellMs / 1000)}s</dd></div>
            <div><dt>touches</dt><dd>{metrics.touches}</dd></div>
          </dl>
          <div className="oracle-actions">
            <button className="primary" onClick={saveRound} disabled={state.matches("completed") || remembered.length === 0}>Save to commons</button>
            {savedSession && <span className="status-pill live">saved locally + queued to commons</span>}
          </div>
          <div className="oracle-detail">
            <span>Gifts: {oracle.gifts.join(" · ")}</span>
            <span>Blind spots: {oracle.blindspots.join(" · ")}</span>
            <span>{oracle.commonsDelta}</span>
          </div>
        </div>
      )}
    </section>
  );
}
